import { useCallback, useRef } from 'react';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void> | void;
  threshold?: number;
  enabled?: boolean;
}

export const usePullToRefresh = ({ 
  onRefresh, 
  threshold = 80, 
  enabled = true 
}: UsePullToRefreshOptions) => {
  const isRefreshing = useRef(false);
  const startY = useRef(0);
  const pullDistance = useRef(0);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || window.scrollY > 0) return;
    startY.current = e.touches[0].clientY;
  }, [enabled]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || window.scrollY > 0 || isRefreshing.current) return;
    
    const currentY = e.touches[0].clientY;
    pullDistance.current = currentY - startY.current;
    
    if (pullDistance.current > 0) {
      // Add visual feedback here if needed
    }
  }, [enabled]);

  const handleTouchEnd = useCallback(async () => {
    if (!enabled || isRefreshing.current) return;
    
    if (pullDistance.current > threshold) {
      isRefreshing.current = true;
      try {
        await onRefresh();
      } finally {
        isRefreshing.current = false;
        pullDistance.current = 0;
      }
    }
    
    pullDistance.current = 0;
  }, [enabled, threshold, onRefresh]);

  const bindToElement = useCallback((element: HTMLElement | null) => {
    if (!element) return;
    
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { bindToElement, isRefreshing: isRefreshing.current };
};